import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, Trash2 } from 'lucide-react';

// Shared client-portal generator: date range + multiple position lines →
// one row per (date × position). Used in New Request and Add More Staff.
export default function RangeGenerator({ positions, onGenerate, onCancel, busy = false, generateLabel = 'Generate Days' }) {
  const [range, setRange] = useState({ from: '', to: '', start_time: '', end_time: '' });
  const [lines, setLines] = useState([{ position: '', staff_required: 1 }]);

  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const generate = () => {
    if (!range.from || !range.to || !range.start_time || !range.end_time) {
      toast.error('Complete the date range and times');
      return;
    }
    const from = new Date(`${range.from}T00:00:00`);
    const to = new Date(`${range.to}T00:00:00`);
    if (to < from) { toast.error('End date must be on or after the start date'); return; }
    const dayMs = 86400000;
    const count = Math.round((to - from) / dayMs) + 1;
    if (count > 31) { toast.error('Maximum 31 days per range'); return; }
    if (lines.some((l) => !l.position || !(parseInt(l.staff_required, 10) >= 1))) {
      toast.error('Every position line needs a position and at least 1 staff');
      return;
    }
    const seen = new Set();
    for (const l of lines) {
      if (seen.has(l.position)) { toast.error(`Duplicate position: ${l.position}`); return; }
      seen.add(l.position);
    }
    const rows = [];
    for (let i = 0; i < count; i++) {
      const d = new Date(from.getTime() + i * dayMs);
      const date = d.toISOString().slice(0, 10);
      for (const l of lines) {
        rows.push({
          date,
          start_time: range.start_time,
          end_time: range.end_time,
          position: l.position,
          staff_required: parseInt(l.staff_required, 10),
          notes: '',
        });
      }
    }
    onGenerate(rows);
  };

  return (
    <Card className="bg-zinc-50 border-zinc-300 border-dashed rounded-lg" data-testid="range-generator">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">
          Add multiple days &amp; positions at once — same period and times for all
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider">From *</Label>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="rounded-md h-9 text-sm" data-testid="range-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider">To *</Label>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="rounded-md h-9 text-sm" data-testid="range-to" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider">Start *</Label>
            <Input type="time" value={range.start_time} onChange={(e) => setRange({ ...range, start_time: e.target.value })} className="rounded-md h-9 text-sm" data-testid="range-start" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider">End *</Label>
            <Input type="time" value={range.end_time} onChange={(e) => setRange({ ...range, end_time: e.target.value })} className="rounded-md h-9 text-sm" data-testid="range-end" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-wider">Positions &amp; staff per day *</Label>
            <Button size="sm" variant="outline" onClick={() => setLines((p) => [...p, { position: '', staff_required: 1 }])} className="rounded-md border-zinc-300 text-xs h-7" data-testid="add-position-line-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Position
            </Button>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`position-line-${i}`}>
              <Select value={l.position} onValueChange={(v) => setLine(i, { position: v })}>
                <SelectTrigger className="rounded-md h-9 text-sm flex-1" data-testid={`line-position-${i}`}>
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  {positions.map((p) => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number" min="1" value={l.staff_required}
                onChange={(e) => setLine(i, { staff_required: e.target.value })}
                className="rounded-md h-9 text-sm w-24" placeholder="Staff"
                data-testid={`line-staff-${i}`}
              />
              {lines.length > 1 && (
                <Button size="icon" variant="ghost" onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))} className="h-8 w-8 text-zinc-400 hover:text-red-500 shrink-0" data-testid={`remove-line-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <Button size="sm" onClick={generate} disabled={busy} className="rounded-md bg-[#14181f] text-white hover:bg-[#232a35] text-xs" data-testid="range-generate-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> {busy ? 'Working…' : generateLabel}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel} className="rounded-md text-xs" data-testid="range-cancel-btn">
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
